"use client";

import React from "react";
import Link from "next/link";
import { v3, inkAccent } from "@/lib/theme";
import { V3SectionHead } from "./V3SectionHead";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

export function V3SectionPricing() {
  const tiers = [
    {
      name: "Free",
      price: "0 €",
      unit: "für immer",
      kicker: "Bis 3 Objekte",
      cta: "Kostenlos starten",
      ctaPrimary: false,
      features: [
        "Rendite, Cashflow, Marktwert",
        "Live-Dashboard & Tabs",
        "Bezirksindex (monatlich)",
        "PDF-Export · 3 Seiten",
        "Verschlüsselte Cloud · EU-Server",
      ],
    },
    {
      name: "Pro",
      price: "12 €",
      unit: "pro Monat",
      kicker: "Unbegrenzt Objekte",
      cta: "Pro testen · 30 Tage gratis",
      ctaPrimary: true,
      features: [
        "Alles aus Free",
        "Bankenpräsentation · 7 Seiten",
        "Zinsalarm · automatische Umschuldungs-Hinweise",
        "Stresstest & DSCR / LTV pro Objekt",
        "§28 EStG-Berechnung",
        "Persönlicher Support per E-Mail",
      ],
      highlight: true,
    },
  ];

  return (
    <motion.section 
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      style={{ padding: "120px 56px" }} className="rp-section"
    >
      <V3SectionHead n={8} kicker="Preise"
        title="Lass dein Imperium wachsen."
        dimWord="Starte kostenlos."
        sub="Keine versteckten Gebühren. Keine Lock-ins. Du kannst dein Portfolio jederzeit exportieren und gehen." />

      <div className="rp-stack mx-auto" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, maxWidth: 1100 }}>
        {tiers.map((t, i) => {
          const isPro = t.highlight;
          return (
            <div key={i} style={{
              background: isPro && v3.intense ? `linear-gradient(180deg, ${v3.accentBg} 0%, ${v3.surf} 60%)` : v3.surf,
              border: `1px solid ${isPro ? v3.accentDim : v3.line}`,
              borderRadius: 18,
              padding: 36,
              position: "relative",
              boxShadow: isPro && v3.intense ? `0 0 0 1px ${v3.accentBg}, 0 24px 80px -40px ${v3.accentDim}` : "none",
            }}>
              {isPro && (
                <div style={{ position: "absolute", top: 18, right: 18, padding: "4px 10px", borderRadius: 999, background: v3.accent, color: v3.bg, font: `600 11px ${v3.mono}`, letterSpacing: "0.06em" }}>
                  EMPFOHLEN
                </div>
              )}
              <div style={{ font: `13px ${v3.sans}`, color: isPro ? v3.accent : v3.dim, fontWeight: 500 }}>{t.name}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                <span style={{ font: `500 72px ${v3.sans}`, color: v3.fg, letterSpacing: "-0.04em", lineHeight: 1 }}>{t.price}</span>
                <span style={{ font: `15px ${v3.sans}`, color: v3.dim }}>{t.unit}</span>
              </div>
              <div style={{ font: `13px ${v3.mono}`, color: v3.dim, marginTop: 6 }}>{t.kicker}</div>

              <Link href="/register">
                <Button 
                  variant={t.ctaPrimary ? "default" : "outline"} 
                  className="w-full mt-7" 
                  size="lg"
                >
                  {t.cta}
                </Button>
              </Link>

              <div style={{ marginTop: 28, paddingTop: 24, borderTop: `1px solid ${isPro ? v3.accentDim : v3.line}` }}>
                {t.features.map((f, fi) => (
                  <div key={fi} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", font: `14px ${v3.sans}`, color: v3.fg2 }}>
                    <span style={{ width: 14, height: 14, background: v3.accent, borderRadius: 99, flex: "0 0 14px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      <svg viewBox="0 0 10 10" width="9" height="9"><path d="M2 5 L4 7 L8 3" stroke={v3.bg} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </span>
                    {f}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 24, font: `12px ${v3.mono}`, color: v3.dim, textAlign: "center" }}>
        Alle Preise inkl. 20 % USt. · Monatlich oder jährlich kündbar. · SEPA, Kreditkarte, Apple Pay.
      </div>
    </motion.section>
  );
}
